import { core } from '@servicetitan/anvil2/token';
import { LoanStatus2 } from '@servicetitan/financing-api-client';
import { PrequalificationStatus } from '@servicetitan/financing-api-client/src/prequals-api';

export const isApprovedStatus = (status: LoanStatus2): boolean => {
    return [
        LoanStatus2.Approved,
        LoanStatus2.Signed,
        LoanStatus2.Prequalified,
        LoanStatus2.Conditional,
        LoanStatus2.CounterOffer,
    ].includes(status);
};

export const isDeclinedStatus = (status: LoanStatus2): boolean => {
    return [
        LoanStatus2.Declined,
        LoanStatus2.Error,
        LoanStatus2.Invalid,
        LoanStatus2.Cancelled,
        LoanStatus2.Expired,
    ].includes(status);
};

export const isPendingStatus = (status: LoanStatus2): boolean => {
    return [
        LoanStatus2.NotSet,
        LoanStatus2.Submitted,
        LoanStatus2.ActivationRequested,
        LoanStatus2.Review,
        LoanStatus2.AboutToSubmit,
        LoanStatus2.NotSigned,
    ].includes(status);
};

export const shouldShowMaxApprovedAmount = (status: LoanStatus2): boolean => {
    // Hide max approved amount if loan is declined or pending
    return !isDeclinedStatus(status) && !isPendingStatus(status);
};

export interface ActionRequiredLoan {
    hasOutstandingTasks?: boolean | null;
    status?: string | LoanStatus2 | null;
    providerName?: string | null;
    authorizations?: unknown[] | null;
}

// Outstanding tasks should not be surfaced once a loan reaches a declined-like
// terminal state — there is nothing actionable for the technician to do.
// Synchrony has an implicit "Create Sales Slip" task that backend
// hasOutstandingTasks doesn't track, so we treat it as outstanding once the
// loan is approved (sales slip creation requires an approved loan) and no
// authorization exists yet.
export const shouldShowActionRequired = (loan: ActionRequiredLoan | null | undefined): boolean => {
    if (!loan?.status) {
        return false;
    }
    const normalizedStatus = normalizeLoanStatus(loan.status);
    if (isDeclinedStatus(normalizedStatus)) {
        return false;
    }
    if (
        loan.providerName?.toLowerCase() === 'synchrony' &&
        isApprovedStatus(normalizedStatus) &&
        !loan.authorizations?.length
    ) {
        return true;
    }
    return !!loan.hasOutstandingTasks;
};

// Normalizes a backend status string to a LoanStatus2 enum value (if possible)
export const normalizeLoanStatus = (status: string | LoanStatus2): LoanStatus2 => {
    if (typeof status !== 'string') {
        return status;
    }
    // Try to match ignoring case and underscores
    const normalized = status.replace(/_/g, '').toLowerCase();
    for (const value of Object.values(LoanStatus2)) {
        if (value.replace(/_/g, '').toLowerCase() === normalized) {
            return value as LoanStatus2;
        }
    }
    return status as LoanStatus2;
};

// Map: Status Enum -> Display Text
// This is the single source of truth for how statuses are displayed
const STATUS_TO_DISPLAY: Record<LoanStatus2, string> = {
    [LoanStatus2.Approved]: 'Approved',
    [LoanStatus2.NotSigned]: 'Approved', // A GS status meaning offer approved, accepted, but not activated.
    [LoanStatus2.Conditional]: 'Approved',
    [LoanStatus2.Declined]: 'Declined',
    [LoanStatus2.Review]: 'Review',
    [LoanStatus2.Signed]: 'Signed',
    [LoanStatus2.CounterOffer]: 'Counteroffer',
    [LoanStatus2.Prequalified]: 'Pre-qualified',
    [LoanStatus2.Expired]: 'Expired',
    [LoanStatus2.NotSet]: 'Not Set',
    [LoanStatus2.AboutToSubmit]: 'About To Submit',
    [LoanStatus2.Error]: 'Error',
    [LoanStatus2.Invalid]: 'Invalid',
    [LoanStatus2.Submitted]: 'Submitted',
    [LoanStatus2.ActivationRequested]: 'Activation Requested',
    [LoanStatus2.Cancelled]: 'Cancelled',
};

export const getLoanStatusDisplayText = (
    status: string | LoanStatus2,
    providerName?: string | null,
): string => {
    const normalized = normalizeLoanStatus(status);
    // BuyFin should show "Not Signed" rather than "Approved" for NotSigned
    if (normalized === LoanStatus2.NotSigned && providerName === 'BuyFin') {
        return 'Not Signed';
    }
    // Synchrony has no distinct "Signed" status; it means Approved
    if (normalized === LoanStatus2.Signed && providerName === 'Synchrony') {
        return 'Approved';
    }
    return STATUS_TO_DISPLAY[normalized] || normalized;
};

// Check if a loan status matches any of the selected filter statuses by display text
export const statusMatchesFilter = (
    loanStatus: string | LoanStatus2,
    selectedStatuses: (string | LoanStatus2)[],
    providerName?: string | null,
): boolean => {
    const loanDisplayText = getLoanStatusDisplayText(loanStatus, providerName);
    return selectedStatuses.some(
        selectedStatus => getLoanStatusDisplayText(selectedStatus) === loanDisplayText,
    );
};

// Get status color based on the underlying status value
export const getStatusColor = (status: string | LoanStatus2): string => {
    const normalizedStatus = normalizeLoanStatus(status);
    switch (normalizedStatus) {
        // Green - Approved statuses
        case LoanStatus2.Approved:
        case LoanStatus2.NotSigned:
        case LoanStatus2.Signed:
        case LoanStatus2.CounterOffer:
        case LoanStatus2.Prequalified:
            return core.primitive.ColorGreen100.value;
        // Red - Declined statuses
        case LoanStatus2.Declined:
        case LoanStatus2.Error:
        case LoanStatus2.Cancelled:
        case LoanStatus2.Expired:
            return core.primitive.ColorRed100.value;
        // Yellow - In-progress statuses
        case LoanStatus2.Review:
        case LoanStatus2.Submitted:
        case LoanStatus2.Conditional:
            return core.primitive.ColorYellow200.value;
        // Grey/Neutral - Other statuses
        case LoanStatus2.NotSet:
        case LoanStatus2.AboutToSubmit:
        case LoanStatus2.Invalid:
        case LoanStatus2.ActivationRequested:
        default:
            return core.primitive.ColorNeutral100.value;
    }
};

/**
 * Checks if a status is a final/terminal status that indicates polling should stop.
 * Works with both PrequalificationStatus and LoanStatus2.
 */
export const isFinalStatus = (
    status: PrequalificationStatus | LoanStatus2 | undefined,
): boolean => {
    if (!status) {
        return false;
    }

    // Check PrequalificationStatus final statuses
    if (
        status === PrequalificationStatus.Declined ||
        status === PrequalificationStatus.Approved ||
        status === PrequalificationStatus.Prequalified ||
        status === PrequalificationStatus.Error ||
        status === PrequalificationStatus.Cancelled
    ) {
        return true;
    }

    // Check LoanStatus2 final statuses
    if (
        status === LoanStatus2.Declined ||
        status === LoanStatus2.Approved ||
        status === LoanStatus2.Prequalified ||
        status === LoanStatus2.Error ||
        status === LoanStatus2.Cancelled
    ) {
        return true;
    }

    return false;
};
